export const leadAdvanced = (p) => !!(p && (p.opportunity || p.offer || p.proposal));
/** Pass `progress` whenever the caller can see it. Jacky, 12 October 2026: an opportunity that has
 * already advanced kept its "attention" alert until someone also recorded a disposition, because this
 * rule read the disposition alone — an untouched `new` lead with an opportunity, offer or Proposal was
 * still "active", i.e. a fresh enquiry needing a first response. Progress now places it in the Pipeline.
 * (A booked measure needs no flag: book_measure already records `appointment_made`.) */
export function leadPlacement(lead, progress) {
    if (lead.job_ref)
        return "job";
    // Disposition `stalled` = parked, not lost: the customer paused and may come back. It stays in the
    // Pipeline (under its own "Stalled" phase) rather than becoming history. Checked before the outcome
    // list because crm.v_lead gives such a lead outcome `stalled`, which below means a Job-stage Stalled.
    // Contrast `went_quiet`: outcome `dead`, historical, leaves the Pipeline.
    if (lead.disposition === "stalled")
        return "pipeline";
    if (["won", "lost", "dead", "void", "completed", "stalled"].includes(String(lead.outcome)))
        return "historical";
    if (["went_quiet", "lost_on_price", "lost_other", "not_a_fit", "duplicate", "recorded_in_error", "contacted_no_answer"].includes(String(lead.disposition)))
        return "historical";
    if (["quoted", "appointment_made", "contacted"].includes(String(lead.disposition)))
        return "pipeline";
    const signals = lead.channel_signals;
    if (lead.data_origin === "synced" || signals?.intake_mode === "historical")
        return "historical";
    if (lead.facet !== "accepted")
        return "historical";
    return leadAdvanced(progress) ? "pipeline" : "active";
}
/** The Pipeline's phases, in filter order. `Stalled` is parked work and is NOT part of "All active". */
export const PIPELINE_PHASES = ["Enquiry", "Preparing quote", "Quote sent", "Accepted — create Job", "Stalled"];
export const PIPELINE_ACTIVE_PHASES = PIPELINE_PHASES.filter((p) => p !== "Stalled");
/** Phase of a lead row in GET /api/pipeline. A lead recorded as stalled is Stalled whatever its offer says.
 * `proposalReleased` (Jacky, 23 October 2026): releasing a Proposal sends the customer a private link, so the quote is
 * with the customer — "Quote sent", even though release itself records no disposition or offer. Before this a released
 * Proposal still read "Preparing quote". */
export function pipelinePhase(lead, offerStatus, hasProposal, proposalReleased = false) {
    if (lead.disposition === "stalled")
        return "Stalled";
    if (offerStatus === "accepted")
        return "Accepted — create Job";
    if (offerStatus === "issued")
        return "Quote sent";
    if (proposalReleased)
        return "Quote sent";
    if (offerStatus)
        return "Preparing quote";
    if (lead.disposition === "quoted")
        return "Quote sent";
    return hasProposal ? "Preparing quote" : "Enquiry";
}
/** The disposition that brings a Stalled lead back into the active Pipeline (un-stalling = recording any
 * other disposition; there is no stored "previous disposition" on the lead). `quoted` when the customer
 * already has a quote in hand, otherwise `contacted`. The phase itself is re-derived from the offer and
 * Proposal by `pipelinePhase`, so a draft offer still reads "Preparing quote". */
export function returnFromStalled(offerStatus) {
    return offerStatus === "issued" || offerStatus === "accepted" ? "quoted" : "contacted";
}
//# sourceMappingURL=lead-location.js.map